import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCalendar,
    FiChevronLeft,
    FiChevronRight,
    FiClock,
    FiDollarSign,
    FiEye,
    FiRefreshCw,
    FiSearch,
    FiUsers,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    fetchSupplierPayables,
    supplierPayableListSelector,
    supplierPayablePaginationSelector,
    supplierPayableSummarySelector,
    supplierPayableLoadingSelector,
    supplierPayableErrorSelector,
} from "../../../../../../store/billing/supplierPayable/supplierPayableSlice";

import "./SupplierPayables.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};


const getTodayInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() +
                    1
            ).padStart(
                2,
                "0"
            );

        const day =
            String(
                date.getDate()
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-${day}`;
    };


const getSupplierName = (
    record
) =>
    record
        ?.supplier
        ?.supplierName ||
    record
        ?.supplier
        ?.companyName ||
    "Supplier";


const getSupplierMobile = (
    record
) =>
    record
        ?.supplier
        ?.mobile ||
    "-";


const getSupplierCode = (
    record
) =>
    record
        ?.supplier
        ?.supplierCode ||
    "-";


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierPayablesPage =
    () => {
        const dispatch =
            useDispatch();

        const history =
            useHistory();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const records =
            useSelector(
                supplierPayableListSelector
            ) || [];

        const pagination =
            useSelector(
                supplierPayablePaginationSelector
            ) || {};

        const summary =
            useSelector(
                supplierPayableSummarySelector
            ) || {};

        const loading =
            useSelector(
                supplierPayableLoadingSelector
            ) || {};

        const errors =
            useSelector(
                supplierPayableErrorSelector
            ) || {};


        /*
        |--------------------------------------------------------------------------
        | FILTER STATE
        |--------------------------------------------------------------------------
        */

        const [
            search,
            setSearch,
        ] = useState("");

        const [
            searchInput,
            setSearchInput,
        ] = useState("");

        const [
            status,
            setStatus,
        ] = useState(
            "ACTIVE"
        );

        const [
            asOfDate,
            setAsOfDate,
        ] = useState(
            getTodayInputValue()
        );

        const [
            page,
            setPage,
        ] = useState(
            1
        );

        const [
            limit,
            setLimit,
        ] = useState(
            20
        );

        const [
            sortBy,
            setSortBy,
        ] = useState(
            "outstandingAmount"
        );

        const [
            sortOrder,
            setSortOrder,
        ] = useState(
            "desc"
        );


        /*
        |--------------------------------------------------------------------------
        | LOAD
        |--------------------------------------------------------------------------
        */

        const loadSupplierPayables =
            useCallback(
                async () => {
                    await dispatch(
                        fetchSupplierPayables({
                            page,

                            limit,

                            search,

                            status:
                                status ===
                                "ALL"
                                    ? ""
                                    : status,

                            asOfDate,

                            sortBy,

                            sortOrder,
                        })
                    );
                },
                [
                    dispatch,
                    page,
                    limit,
                    search,
                    status,
                    asOfDate,
                    sortBy,
                    sortOrder,
                ]
            );


        useEffect(
            () => {
                loadSupplierPayables();
            },
            [
                loadSupplierPayables,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SEARCH DEBOUNCE
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                const timer =
                    setTimeout(
                        () => {
                            setSearch(
                                searchInput
                                    .trim()
                            );

                            setPage(
                                1
                            );
                        },
                        350
                    );

                return () =>
                    clearTimeout(
                        timer
                    );
            },
            [
                searchInput,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | OPEN DETAILS
        |--------------------------------------------------------------------------
        */

        const handleView =
            (
                record
            ) => {
                const supplierId =
                    record
                        ?.supplierId ||
                    record
                        ?.supplier
                        ?._id;

                if (
                    !supplierId
                ) {
                    return;
                }

                history.push(
                    `/billing/supplier-payables/${supplierId}`
                );
            };


        /*
        |--------------------------------------------------------------------------
        | PAGINATION VALUES
        |--------------------------------------------------------------------------
        */

        const total =
            Number(
                pagination
                    ?.total ||
                0
            );

        const currentPage =
            Number(
                pagination
                    ?.page ||
                page ||
                1
            );

        const totalPages =
            Math.max(
                Number(
                    pagination
                        ?.totalPages ||
                    1
                ),
                1
            );

        const currentLimit =
            Number(
                pagination
                    ?.limit ||
                limit ||
                20
            );

        const fromRecord =
            total > 0
                ? (
                      currentPage -
                      1
                  ) *
                      currentLimit +
                  1
                : 0;

        const toRecord =
            total > 0
                ? Math.min(
                      currentPage *
                          currentLimit,
                      total
                  )
                : 0;


        /*
        |--------------------------------------------------------------------------
        | SUMMARY CARDS
        |--------------------------------------------------------------------------
        */

        const summaryCards =
            useMemo(
                () => [
                    {
                        label:
                            "Total Outstanding",

                        value:
                            formatCurrency(
                                summary
                                    ?.totalOutstanding
                            ),

                        help:
                            "Total supplier payable",

                        icon:
                            <FiDollarSign />,

                        className:
                            "supplier-payable-summary-primary",
                    },

                    {
                        label:
                            "Overdue Amount",

                        value:
                            formatCurrency(
                                summary
                                    ?.overdueAmount
                            ),

                        help:
                            "Past due supplier bills",

                        icon:
                            <FiClock />,

                        className:
                            "supplier-payable-summary-danger",
                    },

                    {
                        label:
                            "Suppliers Payable",

                        value:
                            toNumber(
                                summary
                                    ?.suppliersPayable
                            ),

                        help:
                            "Suppliers with balance",

                        icon:
                            <FiUsers />,

                        className:
                            "supplier-payable-summary-success",
                    },

                    {
                        label:
                            "Not Due",

                        value:
                            formatCurrency(
                                summary
                                    ?.notDue
                            ),

                        help:
                            "Amount not yet due",

                        icon:
                            <FiCalendar />,

                        className:
                            "supplier-payable-summary-warning",
                    },
                ],
                [
                    summary,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="supplier-payable-page">

                {/* =====================================================
                    HEADER
                ===================================================== */}

                <header className="supplier-payable-header">

                    <div>
                        <small>
                            PURCHASE
                        </small>

                        <h1>
                            Supplier Payables
                        </h1>

                        <p>
                            Monitor supplier outstanding balances, overdue bills
                            and ageing.
                        </p>
                    </div>


                    <button
                        type="button"
                        className="supplier-payable-button"
                        disabled={
                            loading.list
                        }
                        onClick={
                            loadSupplierPayables
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.list
                                    ? "supplier-payable-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                </header>


                {/* =====================================================
                    ERROR
                ===================================================== */}

                {errors.list && (
                    <div className="supplier-payable-alert">
                        <FiAlertCircle />

                        <span>
                            {errors.list}
                        </span>
                    </div>
                )}


                {/* =====================================================
                    SUMMARY
                ===================================================== */}

                <section className="supplier-payable-summary-grid">

                    {summaryCards.map(
                        (
                            card
                        ) => (
                            <div
                                key={
                                    card.label
                                }
                                className={`supplier-payable-summary-card ${card.className}`}
                            >
                                <div className="supplier-payable-summary-top">

                                    <span className="supplier-payable-summary-icon">
                                        {card.icon}
                                    </span>

                                    <span>
                                        {card.label}
                                    </span>

                                </div>

                                <strong>
                                    {card.value}
                                </strong>

                                <small>
                                    {card.help}
                                </small>
                            </div>
                        )
                    )}

                </section>


                {/* =====================================================
                    AGEING CARDS
                ===================================================== */}

                <section className="supplier-payable-ageing-grid">

                    <div>
                        <span>
                            Not Due
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    ?.notDue
                            )}
                        </strong>
                    </div>


                    <div>
                        <span>
                            0 - 30 Days
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    ?.days0To30
                            )}
                        </strong>
                    </div>


                    <div>
                        <span>
                            31 - 60 Days
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    ?.days31To60
                            )}
                        </strong>
                    </div>


                    <div>
                        <span>
                            61 - 90 Days
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    ?.days61To90
                            )}
                        </strong>
                    </div>


                    <div>
                        <span>
                            90+ Days
                        </span>

                        <strong>
                            {formatCurrency(
                                summary
                                    ?.days90Plus
                            )}
                        </strong>
                    </div>

                </section>


                {/* =====================================================
                    FILTERS
                ===================================================== */}

                <section className="supplier-payable-toolbar">

                    <div className="supplier-payable-search">
                        <FiSearch />

                        <input
                            type="text"
                            value={
                                searchInput
                            }
                            placeholder="Search supplier, mobile, GSTIN..."
                            onChange={(
                                event
                            ) =>
                                setSearchInput(
                                    event
                                        .target
                                        .value
                                )
                            }
                        />
                    </div>


                    <select
                        value={
                            status
                        }
                        onChange={(
                            event
                        ) => {
                            setStatus(
                                event
                                    .target
                                    .value
                            );

                            setPage(
                                1
                            );
                        }}
                    >
                        <option value="ALL">
                            All Suppliers
                        </option>

                        <option value="ACTIVE">
                            Active
                        </option>

                        <option value="INACTIVE">
                            Inactive
                        </option>
                    </select>


                    <input
                        type="date"
                        value={
                            asOfDate
                        }
                        onChange={(
                            event
                        ) => {
                            setAsOfDate(
                                event
                                    .target
                                    .value
                            );

                            setPage(
                                1
                            );
                        }}
                    />


                    <select
                        value={
                            sortBy
                        }
                        onChange={(
                            event
                        ) => {
                            setSortBy(
                                event
                                    .target
                                    .value
                            );

                            setPage(
                                1
                            );
                        }}
                    >
                        <option value="outstandingAmount">
                            Outstanding
                        </option>

                        <option value="overdueAmount">
                            Overdue
                        </option>

                        <option value="totalPurchase">
                            Total Purchase
                        </option>

                        <option value="paidAmount">
                            Paid
                        </option>
                    </select>


                    <select
                        value={
                            sortOrder
                        }
                        onChange={(
                            event
                        ) => {
                            setSortOrder(
                                event
                                    .target
                                    .value
                            );

                            setPage(
                                1
                            );
                        }}
                    >
                        <option value="desc">
                            Highest First
                        </option>

                        <option value="asc">
                            Lowest First
                        </option>
                    </select>

                </section>


                {/* =====================================================
                    TABLE
                ===================================================== */}

                <section className="supplier-payable-table-card">

                    {loading.list &&
                    records.length ===
                        0 ? (
                        <div className="supplier-payable-empty">

                            <FiRefreshCw className="supplier-payable-spinner" />

                            <strong>
                                Loading supplier payables...
                            </strong>

                        </div>
                    ) : records.length ===
                        0 ? (
                        <div className="supplier-payable-empty">

                            <FiUsers />

                            <strong>
                                No supplier outstanding found
                            </strong>

                            <span>
                                Supplier balances will appear here when confirmed
                                purchase bills have an outstanding amount.
                            </span>

                        </div>
                    ) : (
                        <div className="supplier-payable-table-wrapper">

                            <table className="supplier-payable-table">

                                <thead>
                                    <tr>
                                        <th>
                                            Supplier
                                        </th>

                                        <th>
                                            Bills
                                        </th>

                                        <th className="text-right">
                                            Purchase
                                        </th>

                                        <th className="text-right">
                                            Debit Notes
                                        </th>

                                        <th className="text-right">
                                            Paid
                                        </th>

                                        <th className="text-right">
                                            Outstanding
                                        </th>

                                        <th className="text-right">
                                            Overdue
                                        </th>

                                        <th>
                                            Oldest Due
                                        </th>

                                        <th>
                                            Action
                                        </th>
                                    </tr>
                                </thead>


                                <tbody>

                                    {records.map(
                                        (
                                            record,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    record
                                                        ?.supplierId ||
                                                    record
                                                        ?.supplier
                                                        ?._id ||
                                                    index
                                                }
                                            >

                                                {/* SUPPLIER */}

                                                <td>
                                                    <div className="supplier-payable-supplier">

                                                        <strong>
                                                            {getSupplierName(
                                                                record
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierCode(
                                                                record
                                                            )}
                                                            {" • "}
                                                            {getSupplierMobile(
                                                                record
                                                            )}
                                                        </span>

                                                    </div>
                                                </td>


                                                {/* BILLS */}

                                                <td>
                                                    <strong>
                                                        {toNumber(
                                                            record
                                                                ?.totalBills
                                                        )}
                                                    </strong>

                                                    {toNumber(
                                                        record
                                                            ?.overdueBills
                                                    ) >
                                                        0 && (
                                                        <small className="supplier-payable-overdue-count">
                                                            {
                                                                record
                                                                    .overdueBills
                                                            }{" "}
                                                            overdue
                                                        </small>
                                                    )}
                                                </td>


                                                {/* PURCHASE */}

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        record
                                                            ?.totalPurchase
                                                    )}
                                                </td>


                                                {/* DEBIT NOTE */}

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        record
                                                            ?.debitNoteAmount
                                                    )}
                                                </td>


                                                {/* PAID */}

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        record
                                                            ?.paidAmount
                                                    )}
                                                </td>


                                                {/* OUTSTANDING */}

                                                <td className="text-right">
                                                    <strong className="supplier-payable-outstanding">
                                                        {formatCurrency(
                                                            record
                                                                ?.outstandingAmount
                                                        )}
                                                    </strong>
                                                </td>


                                                {/* OVERDUE */}

                                                <td className="text-right">
                                                    <strong
                                                        className={
                                                            toNumber(
                                                                record
                                                                    ?.overdueAmount
                                                            ) >
                                                            0
                                                                ? "supplier-payable-overdue"
                                                                : ""
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            record
                                                                ?.overdueAmount
                                                        )}
                                                    </strong>
                                                </td>


                                                {/* DUE DATE */}

                                                <td>
                                                    {formatDate(
                                                        record
                                                            ?.oldestDueDate
                                                    )}
                                                </td>


                                                {/* ACTION */}

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="supplier-payable-view-button"
                                                        onClick={() =>
                                                            handleView(
                                                                record
                                                            )
                                                        }
                                                    >
                                                        <FiEye />

                                                        View
                                                    </button>
                                                </td>

                                            </tr>
                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>
                    )}


                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    <div className="supplier-payable-pagination">

                        <div>
                            Showing{" "}
                            <strong>
                                {fromRecord}
                            </strong>
                            {" - "}
                            <strong>
                                {toRecord}
                            </strong>
                            {" of "}
                            <strong>
                                {total}
                            </strong>
                        </div>


                        <div className="supplier-payable-pagination-controls">

                            <select
                                value={
                                    limit
                                }
                                onChange={(
                                    event
                                ) => {
                                    setLimit(
                                        Number(
                                            event
                                                .target
                                                .value
                                        )
                                    );

                                    setPage(
                                        1
                                    );
                                }}
                            >
                                <option value={10}>
                                    10 / page
                                </option>

                                <option value={20}>
                                    20 / page
                                </option>

                                <option value={50}>
                                    50 / page
                                </option>

                                <option value={100}>
                                    100 / page
                                </option>
                            </select>


                            <button
                                type="button"
                                disabled={
                                    currentPage <=
                                    1
                                }
                                onClick={() =>
                                    setPage(
                                        (
                                            current
                                        ) =>
                                            Math.max(
                                                current -
                                                    1,
                                                1
                                            )
                                    )
                                }
                            >
                                <FiChevronLeft />
                            </button>


                            <span>
                                Page{" "}
                                <strong>
                                    {currentPage}
                                </strong>
                                {" of "}
                                <strong>
                                    {totalPages}
                                </strong>
                            </span>


                            <button
                                type="button"
                                disabled={
                                    currentPage >=
                                    totalPages
                                }
                                onClick={() =>
                                    setPage(
                                        (
                                            current
                                        ) =>
                                            Math.min(
                                                current +
                                                    1,
                                                totalPages
                                            )
                                    )
                                }
                            >
                                <FiChevronRight />
                            </button>

                        </div>

                    </div>

                </section>

            </div>
        );
    };


export default SupplierPayablesPage;